/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React from 'react';
import { ShieldCheck, Loader2, CheckCircle2, XCircle, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

const MODE_COLOR = {
  live: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  test: 'bg-amber-100 text-amber-800 border-amber-200',
  unknown: 'bg-slate-100 text-slate-700 border-slate-200',
  unset: 'bg-slate-100 text-slate-500 border-slate-200',
};
const SOURCE_LABEL = {
  database: 'MongoDB (pannello admin)',
  environment: 'File .env (fallback)',
  unset: 'Non configurato',
};

const Row = ({ label, children }) => (
  <div className="flex items-center justify-between gap-3 p-3 rounded-lg bg-muted/40">
    <span className="text-muted-foreground">{label}</span>
    {children}
  </div>
);

/** Card "Stato attuale" della configurazione Stripe (valori mascherati dal server). */
export const StripeStatusCard = ({ status, loading }) => {
  const modeColor = MODE_COLOR[status?.mode || 'unset'];
  const sourceLabel = SOURCE_LABEL[status?.source || 'unset'];
  return (
    <Card data-testid="stripe-status-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-primary" /> Stato attuale
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Caricamento…
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            <Row label="Stato">
              {status?.configured ? (
                <Badge className="bg-emerald-600 hover:bg-emerald-700" data-testid="stripe-status-badge">
                  <CheckCircle2 className="h-3 w-3 mr-1" /> Configurato
                </Badge>
              ) : (
                <Badge variant="destructive" data-testid="stripe-status-badge">
                  <XCircle className="h-3 w-3 mr-1" /> Non configurato
                </Badge>
              )}
            </Row>
            <Row label="Modalità">
              <Badge variant="outline" className={modeColor} data-testid="stripe-mode-badge">{(status?.mode || 'unset').toUpperCase()}</Badge>
            </Row>
            <Row label="Secret key"><code className="font-mono text-xs" data-testid="stripe-masked-api-key">{status?.masked_api_key || '—'}</code></Row>
            <Row label="Publishable key"><code className="font-mono text-xs" data-testid="stripe-masked-publishable-key">{status?.masked_publishable_key || '—'}</code></Row>
            <Row label="Webhook secret">
              <span className="text-xs" data-testid="stripe-webhook-status">
                {status?.webhook_configured ? (
                  <span className="text-emerald-700 flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> Configurato</span>
                ) : (
                  <span className="text-muted-foreground">Non impostato</span>
                )}
              </span>
            </Row>
            <Row label="Origine"><span className="text-xs" data-testid="stripe-source">{sourceLabel}</span></Row>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

/** Avviso modalità LIVE. */
export const StripeLiveAlert = () => (
  <Alert className="border-emerald-200 bg-emerald-50 dark:bg-emerald-950/20" data-testid="stripe-live-alert">
    <ShieldCheck className="h-4 w-4 text-emerald-700" />
    <AlertTitle className="text-emerald-800">Modalità LIVE attiva</AlertTitle>
    <AlertDescription className="text-emerald-800/90 text-xs">
      I pagamenti processati sono <strong>reali</strong>. Assicurati che il webhook sia
      correttamente configurato sulla dashboard Stripe verso l'URL:{' '}
      <code className="font-mono">/api/telemedicine/webhook/stripe</code>
    </AlertDescription>
  </Alert>
);

/** Guida passo-passo per ottenere le chiavi. */
export const StripeGuideCard = () => (
  <Card className="border-amber-200 bg-amber-50/50 dark:bg-amber-950/10" data-testid="stripe-guide-card">
    <CardHeader className="pb-3">
      <CardTitle className="text-base flex items-center gap-2 text-amber-900 dark:text-amber-100">
        <AlertTriangle className="h-4 w-4" /> Come ottenere le chiavi
      </CardTitle>
    </CardHeader>
    <CardContent className="text-sm space-y-2 text-amber-900/90 dark:text-amber-100/90">
      <ol className="list-decimal list-inside space-y-1">
        <li>Vai su <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer" className="underline font-medium">dashboard.stripe.com/apikeys</a> e copia le tue chiavi.</li>
        <li>Per il webhook: <em>Developers → Webhooks → Add endpoint</em>, URL <code className="font-mono">/api/telemedicine/webhook/stripe</code>, eventi <code>checkout.session.completed</code>, <code>customer.subscription.deleted</code>. Copia il "Signing secret".</li>
        <li>Incolla i valori qui sopra e premi <strong>Salva</strong>. Le nuove visite useranno immediatamente il tuo account.</li>
        <li>Modalità <strong>TEST</strong> per validare senza addebiti reali; <strong>LIVE</strong> per la produzione.</li>
      </ol>
    </CardContent>
  </Card>
);
